import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { disableScroll, enableScroll } from '../../../../helpers/scroll';

import {
  GlobalStyles,
  Overlay,
  ModalContent,
  TRANSITION_DURATION_MS,
} from './AlwaysRenderedModal.styled';

type AlwaysRenderedModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  contentLabel: string;
  contentRef?: React.RefObject<HTMLDivElement>;
  returnFocusTo?: React.RefObject<HTMLElement | null>;
};

/**
 * A modal component that always renders its children in the DOM.
 */
export const AlwaysRenderedModal = (props: AlwaysRenderedModalProps) => {
  const {
    isOpen,
    onRequestClose,
    children,
    contentLabel,
    contentRef: contentRefProp,
    returnFocusTo,
  } = props;

  const internalModalRef = useRef<HTMLDivElement>(null);
  const contentRef = contentRefProp ?? internalModalRef;
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );
  const previousIsOpenRef = useRef(isOpen);

  // Create portal container on mount
  useEffect(() => {
    const container = document.createElement('div');
    container.dataset.modalPortal = 'always-rendered-modal';
    document.body.appendChild(container);
    setPortalContainer(container);

    return () => {
      container.remove();
    };
  }, []);

  // Handle open/close side effects
  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;
    const justOpened = isOpen && !wasOpen;
    const justClosed = !isOpen && wasOpen;

    if (justOpened) {
      // Hide main content from assistive technology
      const mainContent = document.getElementById('__next');
      if (mainContent) {
        mainContent.setAttribute('aria-hidden', 'true');
        mainContent.setAttribute('inert', '');
      }

      disableScroll();
    }

    if (justClosed) {
      // Restore main content visibility
      const mainContent = document.getElementById('__next');
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
        mainContent.removeAttribute('inert');
      }

      enableScroll();

      const elementToFocus = returnFocusTo?.current;
      if (elementToFocus) {
        elementToFocus.focus();
      }
    }

    previousIsOpenRef.current = isOpen;
  }, [isOpen, returnFocusTo]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const mainContent = document.getElementById('__next');
      if (mainContent) {
        mainContent.removeAttribute('aria-hidden');
        mainContent.removeAttribute('inert');
      }
      enableScroll();
    };
  }, []);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onRequestClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onRequestClose]);

  // Handle click outside modal content
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onRequestClose();
    }
  };

  // This is a bit unconventional, but this is needed
  // to allow imperative control over visibility (iOS keyboard workaround)
  // See the ModalContent "visibility" styled-component property comment for the full explanation.
  useEffect(() => {
    if (!contentRef.current) return;

    if (isOpen) {
      contentRef.current.style.visibility = 'visible';
    } else {
      // Delay hiding to allow slide-out animation to complete
      const timeoutId = setTimeout(() => {
        if (contentRef.current) {
          contentRef.current.style.visibility = 'hidden';
        }
      }, TRANSITION_DURATION_MS);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, contentRef]);

  // Move focus into the dialog on open (after visibility is set to 'visible'),
  // unless a consumer has already moved focus inside.
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;
    if (contentRef.current.contains(document.activeElement)) return;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const firstFocusable =
      contentRef.current.querySelector<HTMLElement>(focusableSelector);

    if (firstFocusable) {
      firstFocusable.focus();
    } else {
      contentRef.current.focus();
    }
  }, [isOpen, contentRef]);

  if (!portalContainer) {
    return null;
  }

  return createPortal(
    <>
      <GlobalStyles $isOpen={isOpen} />
      <Overlay
        isOpen={isOpen}
        onClick={handleOverlayClick}
        aria-hidden={!isOpen}
      >
        <ModalContent
          ref={contentRef}
          isOpen={isOpen}
          role="dialog"
          aria-modal={isOpen}
          aria-label={contentLabel}
          tabIndex={-1}
        >
          {children}
        </ModalContent>
      </Overlay>
    </>,
    portalContainer,
  );
};
